import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { AuthContext } from '../context/AuthContext';
import API_BASE_URL from '../config/api';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import './Access.css';

const Roles = () => {
  const { hasPermission } = useContext(AuthContext);
  const navigate = useNavigate();

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);

  const canCreate = hasPermission('roles.create');
  const canUpdate = hasPermission('roles.update');
  const canDelete = hasPermission('roles.delete');

  const fetchRoles = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/admin/roles`);
      setRoles(response.data?.data || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const handleToggleActive = async (role) => {
    setBusyId(role._id);
    try {
      await axios.put(`${API_BASE_URL}/admin/roles/${role._id}`, { isActive: !role.isActive });
      await fetchRoles();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update role');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`Delete the role "${role.name}"? This cannot be undone.`)) return;
    setBusyId(role._id);
    try {
      await axios.delete(`${API_BASE_URL}/admin/roles/${role._id}`);
      await fetchRoles();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to delete role');
    } finally {
      setBusyId(null);
    }
  };

  const filtered = roles.filter((role) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      role.name.toLowerCase().includes(q) ||
      (role.description || '').toLowerCase().includes(q)
    );
  });

  const pagination = usePagination(filtered, { noun: 'roles' });

  return (
    <Layout>
      <div className="container">
        <div className="access-header">
          <div>
            <h1>Roles &amp; Permissions</h1>
            <p>Define what each group of employees is allowed to do.</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div className="access-stats">
              <span className="access-badge">Roles: {roles.length}</span>
            </div>
            {canCreate && (
              <Link to="/sy-admin/roles/new" className="btn btn-primary">
                <span className="btn-icon">+</span> Add New Role
              </Link>
            )}
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="access-toolbar">
          <input
            className="access-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search roles…"
          />
        </div>

        {loading ? (
          <div className="access-table-container">
            <table className="access-table">
              <tbody>
                {[1, 2, 3, 4].map((i) => (
                  <tr key={i}>
                    <td colSpan={6}>
                      <div className="skeleton skeleton-text" style={{ width: '60%', height: 16 }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <span className="empty-state-icon">🔐</span>
            <h2>No roles found</h2>
            <p>Create a role to start giving employees scoped access.</p>
            {canCreate && (
              <Link to="/sy-admin/roles/new" className="btn btn-primary">Add Your First Role</Link>
            )}
          </div>
        ) : (
          <div className="access-table-container">
            <table className="access-table">
              <thead>
                <tr>
                  <th>Role</th>
                  <th>Description</th>
                  <th>Permissions</th>
                  <th>Employees</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagination.items.map((role) => (
                  <tr key={role._id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="access-name">{role.name}</span>
                        {role.isSystem && <span className="access-tag access-tag-system">System</span>}
                      </div>
                    </td>
                    <td className="access-muted">{role.description || '—'}</td>
                    <td>{role.permissions?.length || 0}</td>
                    <td>{role.employeeCount || 0}</td>
                    <td>
                      {canUpdate ? (
                        <label className="status-toggle-label">
                          <div className="toggle-switch">
                            <input
                              type="checkbox"
                              className="toggle-input"
                              checked={role.isActive}
                              disabled={busyId === role._id}
                              onChange={() => handleToggleActive(role)}
                            />
                            <span className={`toggle-slider ${busyId === role._id ? 'loading' : ''}`} />
                          </div>
                          <span className={`status-text ${role.isActive ? 'status-active-text' : 'status-inactive-text'}`}>
                            {role.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </label>
                      ) : (
                        <span className={`access-tag ${role.isActive ? 'access-tag-active' : 'access-tag-inactive'}`}>
                          {role.isActive ? 'Active' : 'Inactive'}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="access-actions">
                        <button
                          onClick={() => navigate(`/sy-admin/roles/edit/${role._id}`)}
                          className="btn btn-edit btn-icon-only"
                          title={canUpdate ? 'Edit role' : 'View role'}
                        >
                          <span className="btn-icon">{canUpdate ? '✏️' : '👁️'}</span>
                        </button>
                        {canDelete && !role.isSystem && (
                          <button
                            onClick={() => handleDelete(role)}
                            className="btn btn-delete btn-icon-only"
                            title="Delete role"
                            disabled={busyId === role._id}
                          >
                            <span className="btn-icon">🗑️</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination {...pagination.props} />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Roles;
